import React, { useMemo, useState } from "react";
import { formatPenaltyDays } from "../../utils/penaltyFormat";
import { splitPenaltiesByMonth } from "../../utils/penaltyMonthHelpers";
import AttachmentList from "./AttachmentList";
import AppealDeadlineClock from "./AppealDeadlineClock";
import "./MyPenaltiesSections.css";

function PenaltyStatusCell({ penalty }) {
  if (penalty.status === "appealed") {
    return (
      <div className="ess-penalty-status-stack">
        <span className="ess-status-badge ess-status-review">
          {penalty.appealStatus || "معلق"}
        </span>
        {penalty.appealDueAt ? (
          <AppealDeadlineClock dueAt={penalty.appealDueAt} compact />
        ) : null}
      </div>
    );
  }

  return (
    <span className="ess-status-badge ess-status-rejected">ساري</span>
  );
}

function PenaltyActionCell({ penalty, onAppeal }) {
  if (penalty.status === "active") {
    return (
      <button
        type="button"
        className="ess-appeal-btn"
        onClick={() => onAppeal(penalty.id)}
      >
        تقديم تظلم
      </button>
    );
  }

  if (penalty.status === "appealed") {
    return <span className="ess-appeal-pending">تم التظلم</span>;
  }

  return <span className="ess-penalty-no-action">—</span>;
}

function PenaltyCard({ penalty, onAppeal }) {
  return (
    <article className="ess-penalty-card">
      <div className="ess-penalty-card-top">
        <h3 className="ess-penalty-card-type">{penalty.type}</h3>
        <PenaltyStatusCell penalty={penalty} />
      </div>
      <dl className="ess-penalty-card-meta">
        <div className="ess-penalty-card-row">
          <dt>التاريخ</dt>
          <dd>{penalty.date}</dd>
        </div>
        <div className="ess-penalty-card-row">
          <dt>الأيام</dt>
          <dd>{formatPenaltyDays(penalty.days)}</dd>
        </div>
        <div className="ess-penalty-card-row">
          <dt>الخصم</dt>
          <dd>{penalty.deductionLabel || "—"}</dd>
        </div>
        <div className="ess-penalty-card-row ess-penalty-card-reason">
          <dt>السبب</dt>
          <dd>{penalty.reason}</dd>
        </div>
      </dl>
      <AttachmentList
        entityType="penalty"
        entityId={penalty.id}
        showDelete={false}
        hideIfEmpty
        lazy
      />
      <div className="ess-penalty-card-action">
        <PenaltyActionCell penalty={penalty} onAppeal={onAppeal} />
      </div>
    </article>
  );
}

function PenaltiesTable({ penalties, onAppeal }) {
  if (!penalties.length) {
    return null;
  }

  return (
    <table className="ess-table">
      <thead>
        <tr>
          <th>نوع الجزاء</th>
          <th>التاريخ</th>
          <th>عدد الأيام</th>
          <th>الخصم</th>
          <th>السبب</th>
          <th>مرفق</th>
          <th>الحالة</th>
          <th>إجراء</th>
        </tr>
      </thead>
      <tbody>
        {penalties.map((pen) => (
          <tr key={pen.id}>
            <td>{pen.type}</td>
            <td>{pen.date}</td>
            <td>{formatPenaltyDays(pen.days)}</td>
            <td>{pen.deductionLabel || "—"}</td>
            <td>{pen.reason}</td>
            <td>
              <AttachmentList
                entityType="penalty"
                entityId={pen.id}
                showDelete={false}
                compact
                hideIfEmpty
                lazy
              />
            </td>
            <td>
              <PenaltyStatusCell penalty={pen} />
            </td>
            <td>
              <PenaltyActionCell penalty={pen} onAppeal={onAppeal} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PenaltiesList({ penalties, onAppeal }) {
  if (!penalties.length) {
    return null;
  }

  return (
    <>
      <div className="ess-penalties-table-wrap">
        <PenaltiesTable penalties={penalties} onAppeal={onAppeal} />
      </div>
      <div className="ess-penalties-cards">
        {penalties.map((penalty) => (
          <PenaltyCard
            key={penalty.id}
            penalty={penalty}
            onAppeal={onAppeal}
          />
        ))}
      </div>
    </>
  );
}

function CollapsibleMonthCard({ title, penalties, onAppeal }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`ess-table-card ess-penalties-section ess-penalties-previous${
        open ? "" : " ess-penalties-collapsed"
      }`}
    >
      <button
        type="button"
        className="ess-penalties-month-toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <h2 className="ess-table-title ess-penalties-toggle-title">{title}</h2>
        <span className="ess-penalties-toggle-side">
          <span className="ess-penalties-toggle-count">
            {penalties.length} جزاء
          </span>
          <span className="ess-penalties-toggle-hint">
            {open ? "إخفاء" : "عرض"}
          </span>
        </span>
      </button>
      {open ? <PenaltiesList penalties={penalties} onAppeal={onAppeal} /> : null}
    </div>
  );
}

export default function MyPenaltiesSections({ penalties = [], onAppeal }) {
  const { currentMonth, currentMonthLabel, otherMonths } = useMemo(
    () => splitPenaltiesByMonth(penalties, new Date(), { recentMonths: 2 }),
    [penalties],
  );

  if (!currentMonth.length && !otherMonths.length) {
    return (
      <div className="ess-table-card ess-penalties-section">
        <h2 className="ess-table-title">الجزاءات</h2>
        <p className="ess-empty-message">لا توجد جزاءات مسجّلة 🎉</p>
      </div>
    );
  }

  return (
    <>
      <div className="ess-table-card ess-penalties-section">
        <h2 className="ess-table-title">جزاءات {currentMonthLabel}</h2>
        {currentMonth.length > 0 ? (
          <PenaltiesList penalties={currentMonth} onAppeal={onAppeal} />
        ) : (
          <p className="ess-empty-message">لا توجد جزاءات في الشهر الحالي 🎉</p>
        )}
      </div>

      {otherMonths.map((group) => (
        <CollapsibleMonthCard
          key={group.monthKey}
          title={`جزاءات ${group.label}`}
          penalties={group.items}
          onAppeal={onAppeal}
        />
      ))}
    </>
  );
}
